import React, { useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useOutsideClick } from "../../hooks";
import "./breadcrumbs.scss";
import { getWindow } from "../../utils";

type TBreadcrumb = {
    title?: string;
    path?: string;
    onClick?: (arg0: string) => void;
    children?: TBreadcrumb[];
};

export type TBreadcrumbs = {
    breadcrumbs: TBreadcrumb[];
    firstItemsDropdown?: boolean;
    firstItemsLength?: number;
};

export const Breadcrumbs = ({ breadcrumbs, firstItemsDropdown = false, firstItemsLength = 2 }: TBreadcrumbs) => {
    const dropdownId = useId();
    const showDropdownButton = firstItemsDropdown && breadcrumbs.length > 1;
    const [openDropdown, setOpenDropdown] = useState(false);
    const dropdownRef = useRef<HTMLLIElement | null>(null);

    useOutsideClick(openDropdown, dropdownRef, () => setOpenDropdown(false));

    const renderLink = (crumb: TBreadcrumb) => {
        return crumb.onClick ? (
            <span onClick={() => crumb.onClick && crumb.onClick(crumb.path ?? "")}>{crumb.title}</span>
        ) : (
            <Link to={crumb.path ?? ""}>{crumb.title}</Link>
        );
    };

    const customizedBreadcrumbs = !showDropdownButton
        ? breadcrumbs
        : breadcrumbs.reduce((acc: TBreadcrumb[], crumb, i) => {
              if (i <= firstItemsLength - 1 && i < breadcrumbs.length - 1) {
                  const obj: TBreadcrumb = {
                      title: crumb.title,
                      path: crumb.path,
                      onClick: crumb.onClick,
                  };

                  const existingCrumbWithChildren = acc.find(crumb => crumb.children?.length);

                  if (existingCrumbWithChildren) {
                      existingCrumbWithChildren.children?.push(obj);
                      return acc;
                  } else {
                      return [
                          ...acc,
                          {
                              title: "...",
                              children: [obj],
                          },
                      ];
                  }
              } else {
                  return [...acc, crumb];
              }
          }, []);

    const ariaLabel = getWindow().__TEXTS__?.["breadcrumbs"] ?? "Breadcrumbs";

    return (
        <nav aria-label={ariaLabel}>
            <ol className="c-breadcrumbs">
                {customizedBreadcrumbs.map(({ title, path, onClick, children }, i) => {
                    const isCurrent = i === customizedBreadcrumbs.length - 1;

                    if (children?.length) {
                        return (
                            <li key={`${title}-${path}`} ref={dropdownRef} className="c-breadcrumbs__item">
                                <button
                                    className="c-breadcrumbs__dropdown-btn"
                                    onClick={() => setOpenDropdown(prevState => !prevState)}
                                    aria-controls={dropdownId}
                                    aria-expanded={openDropdown}
                                    aria-haspopup="menu"
                                >
                                    ...
                                </button>
                                {openDropdown && (
                                    <div
                                        id={dropdownId}
                                        className="c-breadcrumbs__dropdown"
                                        aria-hidden={!openDropdown}
                                        role="menu"
                                    >
                                        <ul>
                                            {children.map(crumb => (
                                                <li key={`${crumb.title}-${crumb.path}`} role="menuitem">
                                                    {renderLink(crumb)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </li>
                        );
                    }
                    return isCurrent ? (
                        <li key={`${title}-${path}`} className="c-breadcrumbs__item c-breadcrumbs__item--current">
                            {title}
                        </li>
                    ) : (
                        <li key={`${title}-${path}`} className="c-breadcrumbs__item">
                            {renderLink({ title, path, onClick })}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
};
